// Permission identifiers. Modeled as a `const` object plus a union type (rather
// than a TS `enum`, which this package's lint bans) so `Permission.Foo` value
// access and `: Permission` type annotations both keep working at call sites.
export const Permission = {
  // Current permissions
  AIBondSearch: 'ai::bond_search',
  AIControlManage: 'ai::control::manage',
  AIProposalBuilder: 'ai::proposal_builder',
  AIHoldingsParser: 'ai::holdings_parser',
  AISkillCreate: 'ai::skill::create',
  AISkillDeleteAll: 'ai::skill::delete#all',
  AISkillDeleteOwn: 'ai::skill::delete#own',
  AISkillRead: 'ai::skill::read',
  AISkillUpdateAll: 'ai::skill::update#all',
  AISkillUpdateOwn: 'ai::skill::update#own',
  AccountRead: 'account::read',
  AccountWrite: 'account::write',
  AccountAccessManagementRead: 'account::access_management::read',
  AccountAccessManagementWrite: 'account::access_management::write',
  AccountAlternateCreate: 'account::alternate::create',
  AccountAlternateRead: 'account::alternate::read',
  AccountSleeveWrite: 'account::sleeve::write',
  AccountTransactionRead: 'account::transaction::read',
  AccountsRead: 'accounts::read',
  AccountGroupCreate: 'account_group::create',
  AccountGroupDelete: 'account_group::delete',
  AccountGroupRead: 'account_group::read',
  AccountGroupUpdate: 'account_group::update',
  AllocationTemplateCreate: 'allocation_template::create',
  AllocationTemplateDelete: 'allocation_template::delete',
  AllocationTemplatePublish: 'allocation_template::publish',
  AllocationTemplateRead: 'allocation_template::read',
  AllocationTemplateUpdate: 'allocation_template::update',
  AllowListUpdate: 'allow_list::update',
  AnalyticsAccess: 'analytics::access', // Renamed from Analytics for naming convention
  AOMSOrderCancel: 'aoms::order::cancel',
  AOMSOrderCreate: 'aoms::order::create',
  AutomationCreate: 'automation::create',
  AutomationDeleteAll: 'automation::delete#all',
  AutomationDeleteOwn: 'automation::delete#own',
  AutomationRead: 'automation::read',
  AutomationRun: 'automation::run',
  AutomationUpdateAll: 'automation::update#all',
  AutomationUpdateOwn: 'automation::update#own',
  BestExecutionAccess: 'best_execution::access',
  BetaOptimizerAccess: 'beta_optimizer::access', // Renamed from BetaOptimizer for naming convention
  CorporateActionsAccess: 'corporate_actions::access', // Renamed from CorporateActions for naming convention
  EducationCertificationRead: 'education::certification::read',
  EducationCourseRead: 'education::course::read',
  FeeCreate: 'fee::create',
  FeeDelete: 'fee::delete',
  FeeUpdate: 'fee::update',
  OMSFeeConfigRead: 'oms::fee_config::read',
  OMSFeeConfigWrite: 'oms::fee_config::write',
  IPOMarketplaceRead: 'ipo::marketplace::read',
  IPONewIssueWrite: 'ipo::new_issue::write',
  ListCreate: 'list::create',
  ListRead: 'list::read',
  ListUpdate: 'list::update',
  ListDelete: 'list::delete',
  ModelCreate: 'model::create',
  ModelDelete: 'model::delete',
  ModelPreview: 'model::preview',
  ModelPublish: 'model::publish',
  ModelRead: 'model::read',
  ModelUpdate: 'model::update',
  MuniPointsAccess: 'munipoints::access',
  ObjectAccessAccountAdmin: 'object_access::account::admin',
  ObjectAccessAccountCreate: 'object_access::account::create',
  ObjectAccessTeamAdmin: 'object_access::team::admin',
  ObjectAccessTeamCreate: 'object_access::team::create',
  ObjectAccessTeamRead: 'object_access::team::read',
  OfferingRead: 'offering::read',
  OfferingWrite: 'offering::write',
  OMSAssetClassTradeEquity: 'oms::asset_class::trade#equity',
  OMSExecutionInstructionCancel: 'oms::execution_instruction::cancel',
  OMSExecutionInstructionCreate: 'oms::execution_instruction::create',
  OMSExecutionRulesetRead: 'oms::execution_ruleset::read',
  OMSExecutionRulesetWrite: 'oms::execution_ruleset::write',
  OMSExecutionRulesetForOrgWrite: 'oms::execution_ruleset::org::write',
  OMSInquiryIOIApprovePriceAll: 'oms::inquiry::ioi::approve_price#all',
  OMSInquiryIOIApprovePriceSelf: 'oms::inquiry::ioi::approve_price#self',
  OMSInquiryPricingRulesetRead: 'oms::inquiry::pricing_ruleset::read',
  OMSInquiryPricingRulesetWrite: 'oms::inquiry::pricing_ruleset::write',
  OMSInquiryQuoteWantedApprovePriceAll:
    'oms::inquiry::quote_wanted::approve_price#all',
  OMSInquiryQuoteWantedApprovePriceSelf:
    'oms::inquiry::quote_wanted::approve_price#self',
  OMSInquiryQuoteWantedQuotePrice: 'oms::inquiry::quote_wanted::quote_price',
  OMSOrderFeesReadAdvisor: 'oms::order::fees::read#advisor',
  OMSOrderFeesReadCustomer: 'oms::order::fees::read#customer',
  OMSOrderFeesReadExecutingBroker: 'oms::order::fees::read#executing_broker',
  OMSOrderFeesReadIntroducingBroker:
    'oms::order::fees::read#introducing_broker',
  OMSOrderCancel: 'oms::order::cancel',
  OMSOrderCreate: 'oms::order::create',
  OMSOrderManualReview: 'oms::order::manual_review',
  OMSOrderReadAll: 'oms::order::read#all',
  OMSOrderReadAdvisor: 'oms::order::read#advisor',
  OMSOrderReadIntroducingBroker: 'oms::order::read#introducing_broker',
  OMSOrderUpdate: 'oms::order::update',
  OMSTradeCancel: 'oms::trade::cancel',
  OrganizationRead: 'organization::read',
  PMSAllocationRead: 'pms::allocation::read',
  PMSAllocationCreate: 'pms::allocation::create',
  PMSAllocationUpdate: 'pms::allocation::update',
  PMSAllocationDelete: 'pms::allocation::delete',
  PMSPortfolioBuilderRead: 'pms::portfolio_builder::read',
  PricingHistoricalAccess: 'pricing_historical::access', // Renamed from PricingHistorical for naming convention
  PricingRealTimeAccess: 'pricing_real_time::access', // Renamed from PricingRealTime for naming convention
  ProposalCancel: 'proposal::cancel',
  ProposalConfigurableObjectiveFunction:
    'proposal::configurable_objective_function',
  ProposalCreate: 'proposal::create',
  ProposalRead: 'proposal::read',
  ProposalReadAll: 'proposal::read#all',
  ProposalReadAssigned: 'proposal::read#assigned',
  ProposalTrade: 'proposal::trade',
  ProposalUpdate: 'proposal::update',
  ProposalUpdateAll: 'proposal::update#all',
  QuoteRead: 'quote::read',
  ReferenceDataAccess: 'reference_data::access', // Renamed from ReferenceData for naming convention
  RFQCancel: 'rfq::cancel',
  RFQCreate: 'rfq::create',
  RFQRead: 'rfq::read',
  RiskAccess: 'risk::access', // Renamed from Risk for naming convention
  RiskClearingBlocklistCreate: 'risk::clearing_blocklist::create',
  RiskClearingBlocklistDelete: 'risk::clearing_blocklist::delete',
  RiskControlCreate: 'risk::control::create',
  RiskControlDelete: 'risk::control::delete',
  RiskControlRead: 'risk::control::read',
  RiskControlUpdate: 'risk::control::update',
  RiskGroupRead: 'risk::group::read',
  RiskGroupUpdate: 'risk::group::update',
  RiskTradingBreakRead: 'risk::trading_break::read',
  RiskTradingBreakUpdate: 'risk::trading_break::update',
  RiskTradingHaltLift: 'risk::trading_halt::lift',
  RiskTradingHaltPlace: 'risk::trading_halt::place',
  RoleCreate: 'role::create',
  RoleDelete: 'role::delete',
  RoleRead: 'role::read',
  RoleUpdate: 'role::update',
  SchemaDataWrite: 'schema::data::write',
  SchemaStructureWrite: 'schema::structure::write',
  StructuredInvestmentRead: 'structured_investment::read',
  SuboptimalSandboxAccess: 'suboptimal_sandbox::access', // Renamed from SuboptimalSandbox for naming convention
  TradingBreakRead: 'trading::break::read',
  TradingBreakUpdate: 'trading::break::update',
  TraderProfileCreate: 'trader_profile::create',
  TraderProfileRead: 'trader_profile::read',
  TraderProfileUpdate: 'trader_profile::update',
  UserCreate: 'user::create',
  UserDelete: 'user::delete',
  UserRead: 'user::read',
  UserUpdate: 'user::update',
  WorkflowCreate: 'workflow::create',
  WorkflowDelete: 'workflow::delete',
  WorkflowRead: 'workflow::read',
  WorkflowUpdate: 'workflow::update',
  MarkupRead: 'markup::read',
  LPLEditCheckRead: 'lpl::edit_check::read',
  LPLEditCheckCreate: 'lpl::edit_check::create',
} as const

export type Permission = (typeof Permission)[keyof typeof Permission]

// Check-only wildcard permissions. These ARE NEVER stored as grants. They exist
// only so  callers can ask "does this principal have any scope of this permission?"
// with a named constant instead of a literal string.
export const CheckOnlyPermission = {
  AISkillDeleteAny: 'ai::skill::delete#*',
  AISkillUpdateAny: 'ai::skill::update#*',
  AutomationDeleteAny: 'automation::delete#*',
  AutomationUpdateAny: 'automation::update#*',
  OMSInquiryIOIApprovePriceAny: 'oms::inquiry::ioi::approve_price#*',
  OMSInquiryQuoteWantedApprovePriceAny:
    'oms::inquiry::quote_wanted::approve_price#*',
  OMSOrderReadAny: 'oms::order::read#*',
  OMSOrderFeesReadAny: 'oms::order::fees::read#*',
  ProposalReadAny: 'proposal::read#*',
  ProposalUpdateAny: 'proposal::update#*',
} as const

export type CheckOnlyPermission =
  (typeof CheckOnlyPermission)[keyof typeof CheckOnlyPermission]

// Union of permissions that are valid as the required input to a permission
// check (component props, hook args, helpers). Grant-side types (the user's
// actual permissions from the backend) stay as `Permission` — the wildcard
// is never stored as a grant.
export type PermissionCheck = Permission | CheckOnlyPermission
